import { ArrowRightIcon, SplitArrowIcon } from "@xola/icons";
import { isExperiencePurchaseItem } from "common";
import dayjs from "dayjs";
import React from "react";
import { Audit, ExperiencePurchaseItem, Package, PartnerPackage, Purchase, PurchaseItem } from "types";
import { ExperienceTile } from "../ExperienceTile";
import { TimelineItem, TimelinePill } from "../TimelineItem";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { useProduct } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";

interface PackageSplitTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const PackageSplitTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: PackageSplitTimelineItemProps) => {
    const seller = useSeller();
    const packageName = getOriginalPackageName(audit);
    const packageId = getOriginalPackageId(audit);
    const itemCount = countOriginalItems(audit);
    const experienceItems = purchase.items.filter((purchaseItem): purchaseItem is ExperiencePurchaseItem =>
        isExperiencePurchaseItem(purchaseItem),
    );

    const { product: packageProduct } = useProduct<Package | PartnerPackage>(packageId ?? undefined, null, {
        enabled: !!packageId,
    });

    if (!PackageSplitTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Package Split"
            audit={audit}
            icon={<SplitArrowIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="flex items-start space-x-4">
                {packageName && (
                    <div className="relative">
                        <TimelinePill>
                            <div className="flex flex-row">
                                {packageProduct && (
                                    <ProductImage product={packageProduct} size="small" className="mr-1" />
                                )}
                                <div className="flex flex-col justify-between py-1">
                                    <NoTranslate as="div" className="!whitespace-normal pb-1 font-bold">
                                        {packageName}
                                    </NoTranslate>
                                </div>
                            </div>
                        </TimelinePill>
                        {itemCount > 0 && (
                            <span className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue text-xs font-bold text-white">
                                {itemCount}
                            </span>
                        )}
                    </div>
                )}
                <ArrowRightIcon className="mt-2" />
                <div className="flex flex-col space-y-2">
                    {experienceItems.map((purchaseItem) => (
                        <TimelinePill key={purchaseItem.id}>
                            <ExperienceTile
                                item={purchaseItem}
                                experienceId={purchaseItem.product.id}
                                arrivalDateTime={dayjs(purchaseItem.arrivalDateTime).tz(seller.timezoneName)}
                            />
                        </TimelinePill>
                    ))}
                </div>
            </div>
        </TimelineItem>
    );
};

type AuditItemEntry = {
    parentProduct?: { old?: { id?: string; object?: string } | null; new?: unknown };
    parentProductName?: { old?: string };
};

const getAuditItemEntries = (audit: Audit): AuditItemEntry[] => {
    const items = audit?.data?.items;
    if (Array.isArray(items)) {
        return items as AuditItemEntry[];
    }
    if (items && typeof items === "object") {
        return Object.values(items) as AuditItemEntry[];
    }
    return [];
};

PackageSplitTimelineItem.supportsAudit = (_purchase: Purchase, _item: PurchaseItem | null, audit: Audit) => {
    const entries = getAuditItemEntries(audit);
    if (entries.length === 0) {
        return false;
    }
    // Split package is the only op that nulls parentProduct on every item of the purchase.
    return entries.every((entry) => entry?.parentProduct?.old && entry?.parentProduct?.new === null);
};

const getOriginalPackageName = (audit: Audit): string | null => {
    for (const entry of getAuditItemEntries(audit)) {
        const oldName = entry?.parentProductName?.old;
        if (oldName) {
            return oldName;
        }
    }
    return null;
};

const getOriginalPackageId = (audit: Audit): string | null => {
    for (const entry of getAuditItemEntries(audit)) {
        const oldId = entry?.parentProduct?.old?.id;
        if (oldId) {
            return oldId;
        }
    }
    return null;
};

const countOriginalItems = (audit: Audit): number => {
    return getAuditItemEntries(audit).length;
};
